import z, { ZodTypeAny } from 'zod';

export const cancelReasonMap = {
  hardToUse: '사용이 어려워요',
  missingFeature: '원하는 기능이 없어요',
  thereWasProblem: '서비스 이용에 문제가 있었어요',
  priceConcern: '가격이 부담스러워요',
  planDueEnd: '계획한 사용 기간이 끝났어요',
  other: '기타',
} as const;

export type CancelReasonKey = keyof typeof cancelReasonMap;
export type CancelReason = `cancelReason.${CancelReasonKey}`;

const cancelReasonSchema = z.object<{
  [K in CancelReasonKey]?: ZodTypeAny;
}>({
  // '사용이 어려워요'
  hardToUse: z.boolean().optional(),
  // '원하는 기능이 없어요'
  missingFeature: z.boolean().optional(),
  // '서비스 이용에 문제가 있었어요'
  thereWasProblem: z.boolean().optional(),
  // '가격이 부담스러워요'
  priceConcern: z.boolean().optional(),
  // '계획한 사용 기간이 끝났어요'
  planDueEnd: z.boolean().optional(),
  // '기타'
  other: z.boolean().optional(),
});

// ******************************************************

export const hardToUseFeaturesMap = {
  makeMap: '맵 제작 및 수정이 어려워요',
  settingMap: '맵 세팅 (설정)을 하기 어려워요',
  operation: '운영이 어려워요',
  mobile: '모바일에서 사용이 힘들어요',
  specificFunction: '특정 기능을 사용하기 어려워요',
  otherFeatures: '기타',
} as const;

export type HardToUseFeatureKey = keyof typeof hardToUseFeaturesMap;
export type HardToUseFeature = `hardToUseFeatures.${HardToUseFeatureKey}`;

const hardToUseFeaturesSchema = z.object<{
  [K in HardToUseFeatureKey]?: ZodTypeAny;
}>({
  // 맵 제작 및 수정이 어려워요
  makeMap: z.boolean().optional(),
  // 맵 세팅 (설정)을 하기 어려워요
  settingMap: z.boolean().optional(),
  // 운영이 어려워요
  operation: z.boolean().optional(),
  // 모바일에서 사용이 힘들어요
  mobile: z.boolean().optional(),
  // 특정 기능을 사용하기 어려워요
  specificFunction: z.boolean().optional(),
  // 기타 기능
  otherFeatures: z.boolean().optional(),
});

// ******************************************************

export const thereWasProblemsMap = {
  rtcSharingProblem: '비디오/오디오 공유에 문제가 있었어요',
  connectionProblem: '스페이스 접속이 원활하지 않았어요',
  flickeringProblem: '사용 시 버벅임이 심해요',
  otherFeatures: '기타',
} as const;

export type ThereWasProblemsKey = keyof typeof thereWasProblemsMap;
export type ThereWasProblems = `thereWasProblems.${ThereWasProblemsKey}`;

const thereWasProblemsSchema = z.object<{
  [K in ThereWasProblemsKey]?: ZodTypeAny;
}>({
  // 비디오/오디오 공유에 문제가 있었어요
  rtcSharingProblem: z.boolean().optional(),
  // 스페이스 접속이 원활하지 않았어요
  connectionProblem: z.boolean().optional(),
  // 사용 시 버벅임이 심해요
  flickeringProblem: z.boolean().optional(),
  // 기타 기능
  otherFeatures: z.boolean().optional(),
});

// ******************************************************

// 추후 예, 아니오가 아닌 다른 답변이 추가될 경우를 대비하여 배열로 정의
export const planDueEndAnswerMap = ['네', '아니오'] as const;
export type PlanDueEndAnswer = (typeof planDueEndAnswerMap)[number];

// ******************************************************

// 가격 구간 맵 정의
export const priceConcernAnswerMap = {
  KRW: [
    '₩1-10',
    '₩11-30',
    '₩31-50',
    '₩51-100',
    '₩101-200',
    '₩201-500',
    '₩501-1000',
    '₩1000',
  ],
  USD: [
    '$10-100',
    '$100-300',
    '$300-500',
    '$500-1000',
    '$1000-2000',
    '$2000-5000',
    '$5000-10000',
    '$10000',
  ],
} as const;

// 타입 정의
export type PriceConcernAnswerKey = keyof typeof priceConcernAnswerMap;
export type PriceConcernAnswer =
  | (typeof priceConcernAnswerMap.KRW)[number]
  | (typeof priceConcernAnswerMap.USD)[number];

export type PriceConcernTranslationMapType = {
  KRW: Record<(typeof priceConcernAnswerMap.KRW)[number], string>;
  USD: Record<(typeof priceConcernAnswerMap.USD)[number], string>;
};

export const priceConcernTranslationMap: PriceConcernTranslationMapType = {
  KRW: {
    '₩1-10': 'subscription:cancel.price.krw1_10',
    '₩11-30': 'subscription:cancel.price.krw11_30',
    '₩31-50': 'subscription:cancel.price.krw31_50',
    '₩51-100': 'subscription:cancel.price.krw51_100',
    '₩101-200': 'subscription:cancel.price.krw101_200',
    '₩201-500': 'subscription:cancel.price.krw201_500',
    '₩501-1000': 'subscription:cancel.price.krw501_1000',
    '₩1000': 'subscription:cancel.price.krwover_1000',
  },
  USD: {
    '$10-100': 'subscription:cancel.price.usd10_100',
    '$100-300': 'subscription:cancel.price.usd100_300',
    '$300-500': 'subscription:cancel.price.usd300_500',
    '$500-1000': 'subscription:cancel.price.usd500_1000',
    '$1000-2000': 'subscription:cancel.price.usd1000_2000',
    '$2000-5000': 'subscription:cancel.price.usd2000_5000',
    '$5000-10000': 'subscription:cancel.price.usd5000_10000',
    $10000: 'subscription:cancel.price.usdover_10000',
  },
} as const;

// ******************************************************

export const cancelSchema = z
  .object({
    cancelReason: cancelReasonSchema
      .merge(
        z.object({
          otherReason: z.string().optional(),
          priceConcernAnswer: z
            .enum([...priceConcernAnswerMap.KRW, ...priceConcernAnswerMap.USD])
            .optional(),
          planDueEndAnswer: z.enum(planDueEndAnswerMap).optional(),
        }),
      )
      .optional(),
    hardToUseFeatures: hardToUseFeaturesSchema
      .merge(
        z.object({
          specificFunctionReason: z.string().optional(),
          otherReason: z.string().optional(),
        }),
      )
      .optional(),
    missingFeatures: z.string().optional(),
    thereWasProblems: thereWasProblemsSchema
      .merge(
        z.object({
          flickeringProblemReason: z.string().optional(),
          otherReason: z.string().optional(),
        }),
      )
      .optional(),
    additionalOpinions: z.string().optional(),
  })
  // 취소 사유가 하나라도 선택되어야 함
  .refine(data => {
    return Object.values(data.cancelReason || {}).some(Boolean);
  })
  // "사용이 어려워요" 항목이 선택된 경우, 'hardToUseFeatures' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.cancelReason?.hardToUse &&
      !Object.values(data.hardToUseFeatures || {}).some(Boolean)
    );
  })
  // "사용이 어려워요" 하위 중 "특정 기능을 사용하기 어려워요" 가 체크된 경우 'specificFunction'이 체크

  .refine(data => {
    return !(
      data.hardToUseFeatures?.specificFunction &&
      !data.hardToUseFeatures?.specificFunctionReason
    );
  })
  // "기타 기능" 항목이 선택된 경우, 'other' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.hardToUseFeatures?.otherFeatures &&
      !data.hardToUseFeatures?.otherReason
    );
  })

  // "원하는 기능이 없어요" 항목이 선택된 경우 하위의 필요 기능이 필수로 입력되어야 함
  .refine(data => {
    return !(data.cancelReason?.missingFeature && !data.missingFeatures);
  })

  // "서비스 이용에 문제가 있었어요" 항목이 선택된 경우 'thereWasProblemFeatures' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.cancelReason?.thereWasProblem &&
      !Object.values(data.thereWasProblems || {}).some(Boolean)
    );
  })

  // "서비스 이용에 문제가 있었어요" 하위 중 "사용 시 버벅임이 심해요" 가 체크된 경우 'flickeringProblemReason'이 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.thereWasProblems?.flickeringProblem &&
      !data.thereWasProblems?.flickeringProblemReason
    );
  })

  // "가격이 부담스러워요" 항목이 선택된 경우, 'priceConcern' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.cancelReason?.priceConcern && !data.cancelReason?.priceConcernAnswer
    );
  })

  // "계획한 사용 기간이 끝났어요" 항목이 선택된 경우, 'planDueEnd' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.cancelReason?.planDueEnd && !data.cancelReason?.planDueEndAnswer
    );
  })

  // "기타 기능" 항목이 선택된 경우, 'other' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(
      data.thereWasProblems?.otherFeatures &&
      !data.thereWasProblems?.otherReason
    );
  })

  // "기타" 항목이 선택된 경우, 'other' 필드가 필수적으로 입력되어야 함
  .refine(data => {
    return !(data.cancelReason?.other && !data.cancelReason?.otherReason);
  });

export type CancelSchema = z.infer<typeof cancelSchema>;
